import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TextField from "../components/common/form/TextField";
import { validator } from "../utils/validator";
import { useDispatch, useSelector } from "react-redux";
import { getAuthErrors, login } from "../store/users";
import { authErrorText } from "../utils/generateAuthError";
import CaptchaComponent from "../components/CaptchaComponent";
import { useI18n } from "../i18n/LanguageContext";

const LoginForm = memo(() => {
  const [data, setData] = useState({
    email: "",
    password: "",
    stayOn: false,
  });

  const { country } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  const loginError = useSelector(getAuthErrors());
  const { t } = useI18n();

  const handleChange = (target) => {
    setData((prevState) => ({
      ...prevState,
      [target.name]: target.value,
    }));
  };

  const handleCaptcha = () => {
    setData((prevState) => ({
      ...prevState,
      stayOn: true,
    }));
  };

  const validatorConfig = useMemo(
    () => ({
      email: {
        isRequired: {
          message: t("login.err.emailRequired"),
        },
        isEmail: {
          message: t("login.err.email"),
        },
        max: {
          message: t("login.err.emailMax"),
          value: 32,
        },
      },
      password: {
        isRequired: {
          message: t("login.err.passwordRequired"),
        },
        isCapitalSymbol: {
          message: t("login.err.capital"),
        },
        isContainDigit: {
          message: t("login.err.digit"),
        },
        min: {
          message: t("login.err.passwordMin"),
          value: 8,
        },
        max: {
          message: t("login.err.passwordMax"),
          value: 25,
        },
      },
      stayOn: {
        isRequired: {
          message: t("login.err.captcha"),
          value: true,
        },
      },
    }),
    [t]
  );

  useEffect(() => {
    validate();
  }, [data]);

  const validate = useCallback(() => {
    const errors = validator(data, validatorConfig);
    setErrors(errors);
    return Object.keys(errors).length === 0;
  }, [data, validatorConfig]);

  const isValid = Object.keys(errors).length === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = validate();
    if (!isValid) return;
    const redirect = navigate(`/${country}`);
    dispatch(login({ payload: data, redirect }));
  };
  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="rounded space-y-4">
        <TextField
          label={t("login.email")}
          name="email"
          type="email"
          value={data.email}
          onChange={handleChange}
          error={errors.email}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        />
        <TextField
          label={t("login.password")}
          type="password"
          name="password"
          autoComplete="current-password"
          value={data.password}
          onChange={handleChange}
          error={errors.password}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        />
        <div className="flex flex-col w-full justify-center">
          {!data.stayOn && (
            <CaptchaComponent onChange={handleCaptcha} error={errors.message} />
          )}
        </div>
        {loginError && (
          <p className="text-red-500 text-sm px-3 py-2 bg-red-50 rounded border border-red-200">
            {authErrorText(t, loginError)}
          </p>
        )}
        <div className="flex justify-end mt-5">
          <button
            type="submit"
            className="btn-smooth mb-2 mr-2 rounded-lg bg-lime-500 px-5 py-2.5 text-sm font-medium text-black hover:bg-lime-400 focus:outline-none focus:ring-4 focus:ring-lime-300 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!isValid || !data.stayOn}
          >
            {t("login.submit")}
          </button>
        </div>
      </form>
    </div>
  );
});

export default LoginForm;
